/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { ReactNode, useCallback, useRef } from 'react';
import Styles from '../../css/modals/DefaultModal.module.css';
import useSemanticDialog from '../../common/hooks/useSemanticDialog';
import { preventDefaultAndStopPropagation } from '../../common/UtilityFunctions';

type props = {
  open: boolean;
  children?: ReactNode;
  className?: string;
  content?: ReactNode;
  footer?: ReactNode;
  title?: ReactNode;
  unmount?: boolean;
  onClose?: (e?: any) => void;
};

const DefaultModal = ({ open, children, className, content, footer, title, unmount, onClose }: props) => {
  const dialogRef = useRef(null); // Ref to track the current modal element

  // Ensure modal visibility matches searchParams
  useSemanticDialog(dialogRef, 'showModal', open);

  const close = useCallback(
    (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      if (typeof onClose === 'function') onClose();
    },
    [onClose],
  );

  return (
    <dialog
      className={[Styles.dialog, ...(onClose ? [Styles.onClose] : []), ...(className ? [className] : [])].join(' ')}
      ref={dialogRef}
      onClick={close}
    >
      {(!unmount || open) && (
        <section className={className} onClick={preventDefaultAndStopPropagation}>
          {title && (
            <header>
              {title}
              {onClose && (
                <button onClick={close} title='Close'>
                  <svg
                    aria-hidden
                    xmlns='http://www.w3.org/2000/svg'
                    width='25'
                    height='25'
                    fill='currentColor'
                    viewBox='0 0 25 25'
                  >
                    <path
                      fill='currentColor'
                      d='M1.634 1.634a3.134 3.134 0 0 0 0 4.433l6.649 6.649-6.65 6.649a3.134 3.134 0 0 0 4.434 4.433l6.649-6.65 6.649 6.65a3.134 3.134 0 0 0 4.433-4.433l-6.65-6.65 6.65-6.648a3.134 3.134 0 1 0-4.433-4.433l-6.65 6.649-6.648-6.65a3.134 3.134 0 0 0-4.433 0Z'
                    />
                  </svg>
                </button>
              )}
            </header>
          )}
          {children && children}
          {content && content}
          {footer && <footer>{footer}</footer>}
        </section>
      )}
    </dialog>
  );
};

export default DefaultModal;
